import React, { createContext, useContext, useState, ReactNode } from "react";
import { Snackbar, Alert, Stack } from "@mui/material";

interface ErrorContextType {
  errors: string[];
  showErrors: (errors: string[]) => void;
  showError: (error: string) => void;
  showWarning: (warning: string) => void;
  showSuccess: (success: string) => void;
  showSuccessWithWarning: (success: string, warning: string) => void;
  clearError: () => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const ErrorProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [errors, setErrors] = useState<string[]>([]);
  const [warning, setWarning] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const showErrors = (errors: string[]) => {
    setErrors(errors);
    setWarning(null);
    setSuccess(null);
    setOpen(true);
  };

  const showError = (error: string) => {
    setErrors([error]);
    setWarning(null);
    setSuccess(null);
    setOpen(true);
  };

  const showWarning = (message: string) => {
    setWarning(message);
    setErrors([]);
    setSuccess(null);
    setOpen(true);
  };

  const showSuccess = (message: string) => {
    setSuccess(message);
    setErrors([]);
    setWarning(null);
    setOpen(true);
  };

  const showSuccessWithWarning = (successMsg: string, warningMsg: string) => {
    setSuccess(successMsg);
    setWarning(warningMsg);
    setErrors([]);
    setOpen(true);
  };

  const clearError = () => {
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
    // Clear messages after animation completes
    setTimeout(() => {
      setErrors([]);
      setWarning(null);
      setSuccess(null);
    }, 300);
  };

  return (
    <ErrorContext.Provider
      value={{ errors, showError, showErrors, showWarning, clearError, showSuccess, showSuccessWithWarning }}
    >
      {children}
      <Snackbar
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={10000}
      >
        <Stack spacing={1} sx={{ width: "100%" }}>
          {errors.length > 0 && (
            <Alert onClose={handleClose} severity="error">
              {errors.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </Alert>
          )}
          {warning && (
            <Alert onClose={handleClose} severity="warning">
              {warning.split("\n").map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </Alert>
          )}
          {success && (
            <Alert onClose={handleClose} severity="success">
              {success}
            </Alert>
          )}
        </Stack>
      </Snackbar>
    </ErrorContext.Provider>
  );
};

export const useError = (): ErrorContextType => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error("useError must be used within an ErrorProvider");
  }
  return context;
};